/**
 * Markdown helpers.
 *
 * `stripMarkdown` flattens Markdown WE authored for the two surfaces that cannot render it: the
 * conversation-list preview and the CRM chat mirror. It is never pointed at plain-channel text and
 * never at anything an operator typed — both would be guesses, and every wrong guess corrupts
 * ordinary prose. The subset is small (bold/italic, code, links, lists, headings, quotes,
 * strikethrough), so a focused regex pass is enough and keeps this out of the remark pipeline the
 * chat surfaces use for rendering. Links become `text (url)` so the URL survives in plain text.
 */

/**
 * Emphasis, held close to the delimiter discipline CommonMark — and therefore
 * react-markdown — applies, so the helper flattens roughly what would have been
 * rendered and leaves literal text alone. Three rules carry the weight: no
 * whitespace directly inside the markers (`2 * 3 * 4` is arithmetic, not
 * italics), no intraword `_` (`phone_number_id`, `a_b_c`), and no spanning a
 * newline. `*` may sit intraword, matching CommonMark.
 *
 * Two known, deliberate deviations: emphasis across a soft line break stays
 * literal (CommonMark would render it), and the punctuation-flanking clauses
 * are not modelled, so `*Hi!*Bye` flattens although CommonMark would not.
 *
 * The optional tail is lazy (`??`) on purpose. Greedy, it prefers "one more
 * character" over the closer sitting right there, lets `[^\s]` eat the first
 * `*` of the closing run, and binds to a later delimiter instead — which turns
 * `**A**, **B**, **C**` into `A**, **B, C`, the single most common shape an LLM
 * reply takes.
 */
const EMPHASIS_BODY = String.raw`[^\s](?:[^\n]*?[^\s])??`;
const STRONG_ASTERISK_RE = new RegExp(String.raw`\*\*(${EMPHASIS_BODY})\*\*`, 'g');
const EM_ASTERISK_RE = new RegExp(String.raw`\*([^\s*](?:[^\n]*?[^\s*])??)\*`, 'g');
// Word-boundary guards are what keep `_` out of identifiers; `\p{L}` so the
// rule holds for non-Latin words too.
const STRONG_UNDERSCORE_RE = new RegExp(
  String.raw`(?<![\p{L}\p{N}_])__(${EMPHASIS_BODY})__(?![\p{L}\p{N}_])`,
  'gu',
);
const EM_UNDERSCORE_RE = new RegExp(
  String.raw`(?<![\p{L}\p{N}_])_([^\s_](?:[^\n]*?[^\s_])??)_(?![\p{L}\p{N}_])`,
  'gu',
);

export function stripMarkdown(input: string): string {
  let out = input;

  // Images: ![alt](url) → alt
  out = out.replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1');
  // Links: [text](url) → text (url), or just text when the url is empty
  out = out.replace(/\[([^\]]+)\]\(([^)]*)\)/g, (_m, text, url) =>
    url ? `${text} (${url})` : text
  );
  // Fenced code blocks: drop the ``` fences, keep the contents
  out = out.replace(/```[^\n]*\n?/g, '');
  // Inline code: `code` → code
  out = out.replace(/`([^`]+)`/g, '$1');
  // Bold / italic: **x**, __x__, *x*, _x_ → x. Doubles first, so `**x**`
  // never falls through to the single pass as `*` + `x` + `*`.
  out = out.replace(STRONG_ASTERISK_RE, '$1');
  out = out.replace(STRONG_UNDERSCORE_RE, '$1');
  out = out.replace(EM_ASTERISK_RE, '$1');
  out = out.replace(EM_UNDERSCORE_RE, '$1');
  // Strikethrough: ~~x~~ → x
  out = out.replace(/~~(.*?)~~/g, '$1');
  // Headings: leading # ## ### → removed
  out = out.replace(/^\s{0,3}#{1,6}\s+/gm, '');
  // Blockquotes: leading > → removed
  out = out.replace(/^\s{0,3}>\s?/gm, '');
  // Unordered list markers: leading -, *, + → "• "
  out = out.replace(/^\s*[-*+]\s+/gm, '• ');
  // Ordered list markers: leading "1." → "1. " kept as-is is fine; normalize spacing
  out = out.replace(/^\s*(\d+)\.\s+/gm, '$1. ');

  return out.trim();
}
